import { useEffect, type RefObject } from 'react';

/**
 * Focus that is on nothing a screen reader can sit on: `<body>`, a detached
 * node, or one an ancestor has hidden. A reader relocates its cursor as soon as
 * the element stops being rendered, before the browser blurs it.
 */
const isOrphaned = (element: Element | null): boolean => {
  if (!element || element === document.body || !element.isConnected) {
    return true;
  }

  // Resolved, so an ancestor's `visibility: hidden` counts.
  return window.getComputedStyle(element).visibility === 'hidden';
};

/**
 * Gives `target` focus when whatever had it has gone.
 *
 * Accepting a call destroys the button that accepted it, and the browser drops
 * focus to `<body>`, where a screen reader has no position and reads whatever
 * it finds. {@link useFocusRestoration} cannot help: it puts focus *back*, and
 * there is nothing to go back to.
 *
 * The guard is what stops this being focus theft — focus moves only when it is
 * already nowhere, so anyone typing elsewhere is left alone.
 *
 * @param target - Where orphaned focus should land.
 * @param trigger - Re-runs the rescue whenever it changes, for a view that
 * swaps under the user without this hook remounting.
 */
export const useOrphanedFocusRescue = (
  target: RefObject<HTMLElement | null>,
  trigger?: unknown
): void => {
  useEffect(() => {
    // Somebody is somewhere on purpose. Leave them there.
    if (!isOrphaned(document.activeElement)) {
      return;
    }

    target.current?.focus();
  }, [target, trigger]);
};
